/**
 * /pipeline-facility/anomalies — the operator's ILI anomaly list
 * (GET /pipeline-facility/anomalies, fieldiq-api#1219, PF-3a).
 *
 * Full width; a row opens the anomaly on its own page. Filters (site, feature
 * type, not yet dug, search) live in the URL. Import and Add are PF_DISPATCH;
 * both take the site chosen in the filter, so a list is always one site's.
 * Chainage, length and width arrive in the tenant's units (`meta.units`).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Crosshair, FileUp, Plus, Search, Shovel } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { useAuth } from '../../../contexts/AuthContext';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { usePfMeta, usePfSites } from '../../../hooks/usePipelineFacility';
import { usePfAnomalies } from '../../../hooks/usePfDigs';
import { queryLoadFailure } from '../../../lib/load-failure';
import { canDispatchPf, fmtNumber, pfDay, withUnit } from '../../../lib/pipeline-facility';
import type { PfAnomaly } from '../../../types/pf-digs';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useOpenRecord } from '../../downhole/recordPage';
import { PF_EYEBROW, PF_INPUT, PfChip, PfChipRow, PfEmpty, PfPanel, PfRegister, type PfColumn } from '../PfBits';

export default function PfAnomaliesScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const siteId = searchParams.get('siteId') ?? '';
  const featureType = searchParams.get('featureType') ?? '';
  const undug = searchParams.get('undug') ?? '';
  const qParam = searchParams.get('q') ?? '';
  const [search, setSearch] = useState(qParam);

  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };
  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== qParam) setParam('q', search.trim());
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const catalog = usePfMeta().data?.data ?? null;
  const sitesQuery = usePfSites();
  const sites = sitesQuery.data?.data ?? [];
  const filters = useMemo(() => ({ siteId, featureType, undug, q: qParam }), [siteId, featureType, undug, qParam]);
  const filtered = !!(featureType || undug || qParam);
  const listQuery = usePfAnomalies(filters);
  const siteAll = usePfAnomalies({ siteId });
  const rows = listQuery.data?.data ?? [];
  const units = listQuery.data?.meta.units ?? null;
  const all = siteAll.isLoading || queryLoadFailure(siteAll) ? undefined : siteAll.data?.data;
  const countOf = (pick: (a: PfAnomaly) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const canWrite = canDispatchPf(user?.role);
  const siteParam = siteId ? `?siteId=${siteId}` : '';

  const columns: PfColumn<PfAnomaly>[] = [
    {
      key: 'feature',
      header: 'Feature',
      render: (a) => (
        <>
          <p className="font-mono font-extrabold text-white">{a.featureId || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{a.featureTypeLabel || a.featureType || '—'}</p>
        </>
      ),
    },
    { key: 'site', header: 'Site', render: (a) => (<><p>{a.siteName || '—'}</p><p className="mt-0.5 text-xs text-fieldiq-muted">{a.segmentLabel || '—'}</p></>) },
    { key: 'chainage', header: `Chainage${units?.chainage ? ` (${units.chainage})` : ''}`, align: 'right', render: (a) => <span className="font-mono">{fmtNumber(a.chainage)}</span> },
    { key: 'clock', header: 'Clock', render: (a) => <span className="font-mono">{a.clockPosition || '—'}</span> },
    { key: 'depth', header: 'Depth (% WT)', align: 'right', render: (a) => <span className="font-mono">{fmtNumber(a.depthPctWt)}</span> },
    { key: 'size', header: `Length × width${units?.wallThickness ? ` (${units.wallThickness})` : ''}`, align: 'right', render: (a) => <span className="font-mono">{fmtNumber(a.length)} × {fmtNumber(a.width)}</span> },
    { key: 'priority', header: 'Priority', render: (a) => a.priority || '—' },
    { key: 'ili', header: 'ILI run', render: (a) => (<><p>{a.iliVendor || '—'}</p><p className="mt-0.5 text-xs text-fieldiq-muted">{pfDay(a.iliRunDate)}</p></>) },
    { key: 'dig', header: 'Dig', render: (a) => <span className="font-mono">{a.digNumber || '—'}</span> },
  ];

  return (
    <div data-testid="pf-anomalies-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={PF_EYEBROW}
        title="Anomalies"
        description="The operator's in-line inspection list, site by site, and which features have been dug."
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canWrite ? (
            <div className="flex flex-wrap gap-2">
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => openRecord(`/pipeline-facility/anomalies/new${siteParam}`)}>
                <Plus className="size-4" aria-hidden="true" />
                Add anomaly
              </button>
              <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => openRecord(`/pipeline-facility/anomalies/import${siteParam}`)}>
                <FileUp className="size-4" aria-hidden="true" />
                Import list
              </button>
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Crosshair className="size-4" aria-hidden="true" />, label: siteId ? 'On this site' : 'Anomalies', tone: 'neutral', loading: siteAll.isLoading, value: countOf(() => true) },
          { icon: <Shovel className="size-4" aria-hidden="true" />, label: 'On a dig', tone: 'accent', loading: siteAll.isLoading, value: countOf((a) => !!a.digNumber) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <PfChipRow label="Filter by feature type">
            <PfChip label="All" active={!featureType} onClick={() => setParam('featureType', '')} count={siteAll.isLoading ? undefined : countOf(() => true)} />
            {(catalog?.featureTypes ?? []).map((t) => (
              <PfChip
                key={t.value}
                label={t.label}
                active={featureType === t.value}
                onClick={() => setParam('featureType', t.value)}
                count={siteAll.isLoading ? undefined : countOf((a) => a.featureType === t.value)}
              />
            ))}
          </PfChipRow>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_16rem_12rem]">
            <label className="relative block">
              <span className="sr-only">Search feature ID, vendor, priority or notes</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search feature ID, vendor, priority or notes"
                className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </label>
            <select className={PF_INPUT} aria-label="Filter by site" value={siteId} onChange={(e) => setParam('siteId', e.target.value)}>
              <option value="">All sites</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <select className={PF_INPUT} aria-label="Filter by dig" value={undug} onChange={(e) => setParam('undug', e.target.value)}>
              <option value="">Dug and not dug</option>
              <option value="true">Not yet on a dig</option>
            </select>
          </div>

          <PfPanel
            eyebrow="Anomaly list"
            title={siteId ? sites.find((s) => s.id === siteId)?.name ?? 'Anomalies' : 'All sites'}
            testId="pf-anomaly-register"
            footer={!listQuery.isLoading && !queryLoadFailure(listQuery) && rows.length > 0 ? `${rows.length} anomal${rows.length === 1 ? 'y' : 'ies'}` : undefined}
          >
            <PfRegister
              query={listQuery}
              rows={rows}
              columns={columns}
              rail={(a) => (a.digNumber ? 'border-l-fi-green' : 'border-l-fi-steel')}
              onOpen={(a) => openRecord(`/pipeline-facility/anomalies/${a.id}`)}
              subject="anomalies"
              testId="pf-anomaly"
              minWidth="min-w-[1000px]"
              empty={
                filtered ? (
                  <PfEmpty>No anomaly matches these filters.</PfEmpty>
                ) : (
                  <PfEmpty>No anomalies yet{siteId ? ' on this site' : ''}. Import the operator&apos;s ILI list to start.</PfEmpty>
                )
              }
              card={(a) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-extrabold text-white">{a.featureId || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">
                        {a.featureTypeLabel || '—'} · {a.siteName || '—'}
                      </p>
                    </div>
                    <span className="font-mono text-xs text-fieldiq-text">{a.digNumber || 'Not dug'}</span>
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    Chainage {withUnit(a.chainage, units?.chainage)} · Depth {a.depthPctWt == null ? '—' : `${fmtNumber(a.depthPctWt)}%`} · Clock {a.clockPosition || '—'}
                  </p>
                </>
              )}
            />
          </PfPanel>
        </div>
      </section>
    </div>
  );
}
